import { useEffect, useRef, useState } from 'react';

import dayjs from 'dayjs';

import { CELL_WIDTH_REM } from '@/modules/planner/constants';
import { useAutoScroll } from '@/modules/planner/hooks/scroll/use-auto-scroll';
import usePlannerStore from '@/modules/planner/store';
import { EdgeSide } from '@/modules/planner/types/edge-side';

/** Movement threshold (px) before a mousedown counts as a drag rather than a click/hover */
const DRAG_MOVEMENT_THRESHOLD_PX = 2;

interface UseFilterRangeBarDragProps {
  /** TRUE (unclamped) range start — commits apply day shifts to these, preserving any off-screen extent */
  startDate: dayjs.Dayjs;
  /** TRUE (unclamped) range end */
  endDate: dayjs.Dayjs;
  /** Called with the new snapped range on mouseup (only when it actually changed) */
  onCommit: (newStart: dayjs.Dayjs, newEnd: dayjs.Dayjs) => void;
  /** Called once when the movement threshold is crossed (e.g. to hide the tooltip) */
  onDragThresholdCrossed?: () => void;
  disabled?: boolean;
}

interface FilterRangeBarBaseStyles {
  left: string;
  width: string;
}

/**
 * Drag + dual-edge resize for the timeline filter range bar.
 *
 * Mechanics are copied from useDragResizeAssignment.ts (document-level
 * capture-phase listeners, 2px movement threshold, imperative style preview,
 * day snapping on mouseup, Escape cancel, edge auto-scroll). Unlike the
 * assignment hook there is no phantom/optimistic machinery: the bar itself is
 * moved imperatively and the committed filter value re-renders it snapped.
 */
export const useFilterRangeBarDrag = ({
  startDate,
  endDate,
  onCommit,
  onDragThresholdCrossed,
  disabled,
}: UseFilterRangeBarDragProps) => {
  const zoomLevel = usePlannerStore(store => store.zoomLevel);
  const setIsDragResizeInteracting = usePlannerStore(store => store.setIsDragResizeInteracting);

  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState<EdgeSide | null>(null);

  const barRef = useRef<HTMLDivElement>(null);
  const startX = useRef<number | null>(null);
  const didMove = useRef<boolean>(false);
  // Suppresses the click that the browser fires right after a drag's mouseup
  // (cleared shortly after, so a plain click still works)
  const shouldBlockClick = useRef<boolean>(false);
  // The bar's inline left/width at mousedown — restored on cancel, used as the
  // base for resize previews (React owns these styles between interactions).
  const baseStyles = useRef<FilterRangeBarBaseStyles | null>(null);
  const autoScrollStarted = useRef<boolean>(false);
  const { lastMouseXRef, startAutoScroll, stopAutoScroll } = useAutoScroll();

  // Block tooltips globally while interacting (same lock the assignment drag uses)
  useEffect(() => {
    setIsDragResizeInteracting(isDragging || isResizing !== null);
  }, [isDragging, isResizing, setIsDragResizeInteracting]);

  // Release the global lock if the bar unmounts mid-interaction (e.g. filter cleared elsewhere)
  useEffect(() => {
    return () => {
      const store = usePlannerStore.getState();
      if (store.isDragResizeInteracting) {
        store.setIsDragResizeInteracting(false);
      }
    };
  }, []);

  const beginInteraction = (event: React.MouseEvent<HTMLDivElement>) => {
    startX.current = event.clientX;
    lastMouseXRef.current = event.clientX;
    didMove.current = false;
    baseStyles.current = barRef.current
      ? { left: barRef.current.style.left, width: barRef.current.style.width }
      : null;
  };

  const handleBarMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    // Resize handles carry cursor-ew-resize — let them start a resize instead
    if ((event.target as HTMLElement).classList.contains('cursor-ew-resize')) return;
    setIsDragging(true);
    beginInteraction(event);
  };

  const handleResizeMouseDown = (direction: EdgeSide, event: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.stopPropagation();
    setIsResizing(direction);
    beginInteraction(event);
  };

  useEffect(() => {
    const restoreBaseStyles = () => {
      if (!barRef.current) return;
      barRef.current.style.transform = '';
      if (baseStyles.current) {
        barRef.current.style.left = baseStyles.current.left;
        barRef.current.style.width = baseStyles.current.width;
      }
    };

    /** Live preview: move/resize the bar element imperatively (no React re-render per frame) */
    const updateBarPreview = () => {
      if (!barRef.current || startX.current === null || !baseStyles.current) return;
      const changePixels = lastMouseXRef.current - startX.current;
      const remSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const changeRem = changePixels / remSize;
      const baseLeftRem = parseFloat(baseStyles.current.left) || 0;
      const baseWidthRem = parseFloat(baseStyles.current.width) || 0;

      if (isDragging) {
        barRef.current.style.transform = `translateX(${changeRem}rem)`;
      } else if (isResizing === EdgeSide.Left) {
        const clampedChange = Math.min(changeRem, baseWidthRem);
        barRef.current.style.left = `${baseLeftRem + clampedChange}rem`;
        barRef.current.style.width = `${Math.max(0, baseWidthRem - clampedChange)}rem`;
      } else if (isResizing === EdgeSide.Right) {
        barRef.current.style.width = `${Math.max(0, baseWidthRem + changeRem)}rem`;
      }
    };

    const endInteraction = () => {
      setIsDragging(false);
      setIsResizing(null);
      startX.current = null;
      didMove.current = false;
      autoScrollStarted.current = false;
    };

    const handleMouseMove = (e: MouseEvent) => {
      if ((!isDragging && !isResizing) || startX.current === null) return;

      lastMouseXRef.current = e.clientX;
      const changePixels = e.clientX - startX.current;

      if (Math.abs(changePixels) > DRAG_MOVEMENT_THRESHOLD_PX && !didMove.current) {
        didMove.current = true;
        shouldBlockClick.current = true;
        onDragThresholdCrossed?.();
      }

      // Start auto-scroll only after actual movement — prevents instant scroll
      // when mousedown happens near a container edge
      if (didMove.current && !autoScrollStarted.current) {
        autoScrollStarted.current = true;
        startAutoScroll(scrollAmount => {
          if (startX.current !== null) {
            startX.current -= scrollAmount;
          }
          updateBarPreview();
        });
      }

      updateBarPreview();
    };

    const handleMouseUp = (e: MouseEvent) => {
      stopAutoScroll();

      if (!didMove.current || startX.current === null) {
        endInteraction();
        return;
      }

      const remSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const changeRem = (e.clientX - startX.current) / remSize;
      const daysShift = Math.round(changeRem / (CELL_WIDTH_REM * zoomLevel));

      let newStart = startDate;
      let newEnd = endDate;

      if (isDragging) {
        newStart = startDate.add(daysShift, 'day');
        newEnd = endDate.add(daysShift, 'day');
      } else if (isResizing === EdgeSide.Left) {
        newStart = startDate.add(daysShift, 'day');
        if (newStart.isAfter(endDate, 'day')) {
          newStart = endDate;
        }
      } else if (isResizing === EdgeSide.Right) {
        newEnd = endDate.add(daysShift, 'day');
        if (newEnd.isBefore(startDate, 'day')) {
          newEnd = startDate;
        }
      }

      const hasChanged = !newStart.isSame(startDate, 'day') || !newEnd.isSame(endDate, 'day');

      // Always restore the pre-drag inline styles: on commit the new filter value
      // re-renders the bar at the snapped position; without a change nothing moved.
      restoreBaseStyles();
      endInteraction();

      // Clear after a tick so the click handler fired by this mouseup sees the block
      setTimeout(() => {
        shouldBlockClick.current = false;
      }, 100);

      if (hasChanged) {
        onCommit(newStart, newEnd);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && (isDragging || isResizing)) {
        stopAutoScroll();
        restoreBaseStyles();
        endInteraction();
      }
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove, { capture: true, passive: false });
      document.addEventListener('mouseup', handleMouseUp, { capture: true, passive: false });
      document.addEventListener('keydown', handleKeyDown);
      // Prevent day/month labels (and any other content the cursor passes over)
      // from getting text-selected while dragging
      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';
    }

    return () => {
      stopAutoScroll();
      autoScrollStarted.current = false;
      document.removeEventListener('mousemove', handleMouseMove, { capture: true });
      document.removeEventListener('mouseup', handleMouseUp, { capture: true });
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
    };
  }, [
    isDragging,
    isResizing,
    startDate,
    endDate,
    zoomLevel,
    onCommit,
    onDragThresholdCrossed,
    startAutoScroll,
    stopAutoScroll,
    lastMouseXRef,
  ]);

  return {
    barRef,
    isDragging,
    isResizing,
    shouldBlockClick,
    handleBarMouseDown,
    handleResizeMouseDown,
  };
};
